import { useCallback, useEffect, useRef, useState } from 'react';
import { CAlert, CButton, CFormSelect, CSpinner } from '@coreui/react';
import { api, problemMessage, type Schemas } from '../../api/client';
import { downloadFile, formatSize, uploadFile } from '../../api/files';
import { useMe } from '../../auth/AuthGate';
import { RichTextEditor, RichTextView } from '../../components/RichText';
import { Section } from '../../components/Section';
import { formatDateTime } from '../../format';

/** Kdo na zakázce dělá a v jaké roli. Podle toho se pozná, kdo je na tahu. */
export function AssignmentsPanel({ contractId, version, onChanged }: { contractId: number; version: number; onChanged: () => void }) {
  const [assignments, setAssignments] = useState<Schemas['AssignmentResponse'][] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.GET('/api/v1/contracts/{contractId}/assignments', { params: { path: { contractId } } })
      .then(({ data }) => setAssignments(data ?? []));
  }, [contractId]);

  useEffect(load, [load, version]);

  async function remove(id: number) {
    const { error: problem, response } = await api.DELETE('/api/v1/contracts/{contractId}/assignments/{assignmentId}', {
      params: { path: { contractId, assignmentId: id } },
    });

    if (response.ok) { load(); onChanged(); } else setError(problemMessage(problem));
  }

  if (!assignments) return <CSpinner size="sm" />;

  return (
    <Section
      title="Kdo na tom dělá"
      addLabel="Přiřadit"
      tour="add-assignment"
      empty={assignments.length === 0 && !error}
      form={(close) => <AssignForm contractId={contractId} onDone={() => { close(); load(); onChanged(); }} onCancel={close} />}
    >
      {assignments.map((a) => (
        <div key={a.id} className="d-flex justify-content-between align-items-center mb-1">
          <div>
            <span className="fw-semibold">{a.displayName}</span>
            <span className="text-body-secondary small"> · {a.roleName}</span>
          </div>
          <CButton size="sm" color="secondary" variant="ghost" onClick={() => void remove(a.id)} aria-label="Odebrat">×</CButton>
        </div>
      ))}
      {error && <CAlert color="danger" className="mb-0 mt-2">{error}</CAlert>}
    </Section>
  );
}

function AssignForm({ contractId, onDone, onCancel }: { contractId: number; onDone: () => void; onCancel: () => void }) {
  const me = useMe();
  const [members, setMembers] = useState<Schemas['TeamMemberResponse'][]>([]);
  const [roles, setRoles] = useState<Schemas['DomainRoleResponse'][]>([]);
  const [member, setMember] = useState('');
  const [role, setRole] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.GET('/api/v1/team').then(({ data }) => setMembers(data ?? []));
    api.GET('/api/v1/team/roles').then(({ data }) => setRoles(data ?? []));
  }, []);

  // Kdyz clovek vybere kolegu, nabidne se jeho prvni nalepka - nejcastejsi volba.
  function pickMember(id: string) {
    setMember(id);
    const first = members.find((m) => String(m.id) === id)?.roles[0];
    if (first) setRole(first);
  }

  async function add() {
    const { error: problem, response } = await api.POST('/api/v1/contracts/{contractId}/assignments', {
      params: { path: { contractId } },
      body: { teamMemberId: Number(member), roleCode: role, isPrimary: true },
    });

    if (response.ok) onDone(); else setError(problemMessage(problem));
  }

  return (
    <div className="d-flex flex-column gap-2">
      <CFormSelect size="sm" value={member} onChange={(e) => pickMember(e.target.value)} autoFocus>
        <option value="">Koho…</option>
        {members.filter((m) => m.isActive).map((m) => (
          <option key={m.id} value={m.id}>{m.displayName}{m.id === me.memberId ? ' (já)' : ''}</option>
        ))}
      </CFormSelect>
      <CFormSelect size="sm" value={role} onChange={(e) => setRole(e.target.value)}>
        <option value="">Jako…</option>
        {roles.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
      </CFormSelect>
      <div className="d-flex gap-2 justify-content-end">
        <CButton size="sm" color="secondary" variant="ghost" onClick={onCancel}>Zpět</CButton>
        <CButton size="sm" color="primary" disabled={!member || !role} onClick={() => void add()}>Přiřadit</CButton>
      </div>
      {error && <CAlert color="danger" className="mb-0">{error}</CAlert>}
    </div>
  );
}

/** Telefon nebo tablet — tam ma smysl tlacitko „Vyfotit“ (zpetna vazba z porady 9. 10. 2026). */
const isTouchDevice = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

const categoryLabels: Record<string, string> = { Input: 'Podklad', Photo: 'Fotka', Drawing: 'Výkres', Datasheet: 'Datasheet', Other: 'Jiné' };

/**
 * Přílohy. Jedno tlačítko, žádný výběr kategorie — fotka se pozná podle typu souboru,
 * všechno ostatní je podklad. Stahují se přes fetch s tokenem, do URL se token nedává.
 */
/** <c>version</c> se zvedne i po souboru vloženém do textu — ten je taky příloha. */
export function AttachmentsPanel({ contractId, version }: { contractId: number; version?: number }) {
  const [files, setFiles] = useState<Schemas['AttachmentResponse'][] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const camera = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<string | null>(null);

  const load = useCallback(() => {
    api.GET('/api/v1/contracts/{contractId}/attachments', { params: { path: { contractId } } })
      .then(({ data }) => setFiles(data ?? []));
  }, [contractId]);

  useEffect(load, [load, version]);

  // Vic souboru naraz - v terenu se fotek udela vic. Postupne, at je videt, kde to je,
  // a at jedna spatna fotka nezastavi ty ostatni.
  async function upload(list: FileList | null) {
    const picked = [...(list ?? [])];
    if (picked.length === 0) return;

    setBusy(true);
    setError(null);
    const failed: string[] = [];

    for (const [index, file] of picked.entries()) {
      setProgress(picked.length > 1 ? `${index + 1}/${picked.length}` : null);
      const response = await uploadFile(`/api/v1/contracts/${contractId}/attachments`, file, file.type.startsWith('image/') ? 'Photo' : 'Input');
      if (!response.ok) failed.push(`${file.name}: ${problemMessage(await response.json().catch(() => null))}`);
    }

    setBusy(false);
    setProgress(null);
    if (failed.length > 0) setError(failed.join(' · '));
    load();

    if (input.current) input.current.value = '';
    if (camera.current) camera.current.value = '';
  }

  if (!files) return <CSpinner size="sm" />;

  return (
    <>
      <input ref={input} type="file" multiple hidden onChange={(e) => void upload(e.target.files)} />
      {/* capture = rovnou fotoaparat. Na pocitaci se tlacitko neukazuje - tam to nedava smysl. */}
      <input ref={camera} type="file" accept="image/*" capture="environment" hidden onChange={(e) => void upload(e.target.files)} />
      <Section
        title="Přílohy"
        count={files.length}
        empty={files.length === 0 && !error}
        addLabel={busy ? `Nahrávám${progress ? ` ${progress}` : ''}…` : 'Nahrát'}
        extra={isTouchDevice && !busy && (
          <CButton size="sm" color="primary" variant="ghost" onClick={() => camera.current?.click()}>📷 Vyfotit</CButton>
        )}
        // Tady neni co vyplnovat - "+ Nahrát" rovnou otevre vyber souboru.
        onAdd={() => { if (!busy) input.current?.click(); }}
      >
        {files.map((f) => (
          <div key={f.id} className="mb-2">
            <CButton color="link" className="p-0 text-start"
              onClick={() => void downloadFile(`/api/v1/contracts/${contractId}/attachments/${f.id}/content`, f.fileName).catch((e: Error) => setError(e.message))}>
              {f.fileName}
            </CButton>
            <div className="small text-body-secondary">
              {categoryLabels[f.category] ?? f.category} · {formatSize(f.sizeBytes)} · {f.uploadedBy}
            </div>
          </div>
        ))}
        {error && <CAlert color="danger" className="mt-2 mb-0">{error}</CAlert>}
      </Section>
    </>
  );
}

/** Poznámky. Upravit smí kdokoli z týmu — kdo co změnil, je v historii. */
export function NotesSection({ contractId, onChanged }: { contractId: number; onChanged?: () => void }) {
  const [notes, setNotes] = useState<Schemas['NoteResponse'][] | null>(null);
  const [editing, setEditing] = useState<number | null>(null);

  const load = useCallback(() => {
    api.GET('/api/v1/contracts/{contractId}/notes', { params: { path: { contractId } } })
      .then(({ data }) => setNotes(data ?? []));
  }, [contractId]);

  useEffect(load, [load]);

  if (!notes) return null;

  return (
    <Section
      title="Poznámky"
      addLabel="Poznámka"
      empty={notes.length === 0}
      form={(close) => <NoteForm contractId={contractId} onDone={() => { close(); load(); onChanged?.(); }} onCancel={close} onFilesAdded={onChanged} />}
    >
      {notes.map((n) => editing === n.id ? (
        <div key={n.id} className="mb-3">
          <NoteForm contractId={contractId} note={n} onFilesAdded={onChanged}
            onDone={() => { setEditing(null); load(); onChanged?.(); }} onCancel={() => setEditing(null)} />
        </div>
      ) : (
        <div key={n.id} className="mb-3">
          <RichTextView text={n.body} contractId={contractId} />
          <div className="small text-body-secondary d-flex align-items-center gap-2">
            <span>
              {n.authorName} · {formatDateTime(n.createdAt)}
              {n.editedAt && <> · upraveno {formatDateTime(n.editedAt)}</>}
            </span>
            <CButton size="sm" color="primary" variant="ghost" className="py-0" onClick={() => setEditing(n.id)}>Upravit</CButton>
          </div>
        </div>
      ))}
    </Section>
  );
}

/** Nová poznámka, nebo úprava existující (když přijde `note`). */
function NoteForm({ contractId, note, onDone, onCancel, onFilesAdded }: {
  contractId: number; note?: Schemas['NoteResponse']; onDone: () => void; onCancel: () => void; onFilesAdded?: () => void;
}) {
  const [body, setBody] = useState(note?.body ?? '');
  const [error, setError] = useState<string | null>(null);

  async function add() {
    const { error: problem, response } = note
      ? await api.PUT('/api/v1/contracts/{contractId}/notes/{noteId}', {
        params: { path: { contractId, noteId: note.id } }, body: { body },
      })
      : await api.POST('/api/v1/contracts/{contractId}/notes', {
        params: { path: { contractId } }, body: { body, contractItemId: null },
      });

    if (response.ok) onDone(); else setError(problemMessage(problem));
  }

  return (
    <>
      <RichTextEditor value={body} onChange={setBody} placeholder="Co jste zjistili, na co nezapomenout…" autoFocus
        contractId={contractId} onFilesAdded={onFilesAdded} />
      <div className="d-flex gap-2 justify-content-end mt-2">
        <CButton size="sm" color="secondary" variant="ghost" onClick={onCancel}>Zpět</CButton>
        <CButton size="sm" color="primary" disabled={!body} onClick={() => void add()}>Uložit</CButton>
      </div>
      {error && <CAlert color="danger" className="mt-2 mb-0">{error}</CAlert>}
    </>
  );
}

const historyPreview = 3;

/** Kdo co kdy udělal. Standardně jen poslední tři události — celá historie na požádání. */
export function HistorySection({ contractId, version }: { contractId: number; version: number }) {
  const [entries, setEntries] = useState<Schemas['HistoryEntryResponse'][] | null>(null);
  const [all, setAll] = useState(false);

  useEffect(() => {
    api.GET('/api/v1/contracts/{contractId}/history', { params: { path: { contractId }, query: { take: 200 } } })
      .then(({ data }) => setEntries(data ?? []));
  }, [contractId, version]);

  if (!entries) return null;

  const shown = all ? entries : entries.slice(0, historyPreview);

  return (
    <Section title="Historie" empty={entries.length === 0}>
      {shown.map((e, i) => (
        <div key={`${e.correlationId}-${i}`} className="mb-2">
          <div className="small">
            <span className="fw-semibold">{e.actor?.displayName ?? 'Systém'}</span> — {e.action}
            <span className="text-body-secondary"> · {formatDateTime(e.occurredAt)}</span>
          </div>
          {e.comment && <div className="small ps-2 border-start">{e.comment}</div>}
          {all && e.changes.length > 0 && (
            <details className="small">
              <summary className="text-body-secondary">{e.changes.length} změn</summary>
              {e.changes.map((c, j) => (
                <div key={j}>{c.label}: <span className="text-body-secondary">{c.oldValue ?? '—'}</span> → {c.newValue ?? '—'}</div>
              ))}
            </details>
          )}
        </div>
      ))}
      {entries.length > historyPreview && (
        <CButton size="sm" color="link" className="p-0" onClick={() => setAll(!all)}>
          {all ? 'Jen poslední' : `Celá historie (${entries.length})`}
        </CButton>
      )}
    </Section>
  );
}
